import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, ShieldCheck, RefreshCw, Globe, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const BillingToggle = ({ isAnnual, setIsAnnual }) => (
  <div className="flex flex-col items-center mb-10">
    <div className="flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-full">
      <button
        onClick={() => setIsAnnual(false)}
        className={`px-5 py-1.5 rounded-full text-xs font-bold transition-all ${!isAnnual ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
      >
        Monthly
      </button>
      <button
        onClick={() => setIsAnnual(true)}
        className={`px-5 py-1.5 rounded-full text-xs font-bold transition-all ${isAnnual ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
      >
        Yearly
      </button>
    </div>
    {isAnnual && (
      <span className="text-orange-500 text-[10px] font-black uppercase tracking-widest mt-2 animate-pulse">
        Save $10 / Year
      </span>
    )}
  </div>
);

const Feature = ({ text, included = true, highlight = false }) => (
  <li className="flex items-center gap-2 py-0.5">
    <div className="shrink-0">
      {included ? (
        <Check size={14} className={highlight ? 'text-orange-500' : 'text-gray-500'} />
      ) : (
        <X size={14} className="text-gray-800" />
      )}
    </div>
    <span className={`text-xs md:text-sm ${included ? (highlight ? 'text-white font-medium' : 'text-gray-400') : 'text-gray-700'}`}>
      {text}
    </span>
  </li>
);

export default function PricingSection() {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <section id="pricing" className="py-16 md:py-24 container mx-auto px-4 relative">
      <div className="max-w-5xl mx-auto relative z-10">

        <div className="text-center mb-10">
          <h2 className="text-5xl md:text-7xl font-black tracking-tighter text-white mb-4">
            Simple <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-600">Pricing.</span>
          </h2>
          <p className="text-gray-400 text-md md:text-lg max-w-xl mx-auto">
            Free for individuals, powerful for teams.
          </p>
        </div>

        <BillingToggle isAnnual={isAnnual} setIsAnnual={setIsAnnual} />

        <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto">

          {/* --- HOBBY --- */}
          <div className="bg-[#0d0d0d] border border-white/10 rounded-3xl p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white text-xl font-bold">Hobby</h3>
                  <p className="text-gray-500 text-xs">For solo developers & side projects.</p>
                </div>
                <div className="text-2xl font-black text-white">$0</div>
              </div>

              <ul className="space-y-3 mb-8 border-t border-white/5 pt-6">
                <Feature text="2 Projects" />
                <Feature text="1 Team Member / Project" />
                <Feature text="Unlimited Rollbacks" />
                <Feature text="CLI Access" />
                <Feature text="Integrations" />
                <Feature text="1 Service Token / Project"  />
                <Feature text="Unlimited Secrets" />
                <Feature text="3 Day Audit History"  />
              </ul>
            </div>

            <Link to="/register">
              <button className="w-full py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all">
                Get Started
              </button>
            </Link>
          </div>

          {/* --- PRO --- */}
          <div className="relative bg-[#0a0a0a] border-2 border-orange-500/40 rounded-3xl p-6 md:p-8 flex flex-col justify-between shadow-2xl">
            <div className="absolute -top-3 right-6 bg-orange-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
              Popular
            </div>

            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-white text-xl font-bold">Team Pro</h3>
                  <p className="text-gray-500 text-xs">Scale your infrastructure.</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl md:text-4xl font-black text-white">
                    ${isAnnual ? '50' : '5'}
                  </div>
                  <div className="text-[10px] text-gray-500 font-bold uppercase tracking-tighter">
                    {isAnnual ? 'Per Year' : 'Per Month'}
                  </div>
                </div>
              </div>

              <ul className="space-y-3 mb-8 border-t border-orange-500/20 pt-6">
                <Feature text="Up to 30 Projects" highlight />
                <Feature text="12 Team Members / Project" highlight />
                <Feature text="5 Service Tokens (CI/CD) / Project" highlight />
                <Feature text="Integrations" highlight />
                <Feature text="Full Audit Logs" highlight />
                <Feature text="Unlimited Rollbacks" />
                <Feature text="Unlimited Secrets" />
                <Feature text="CLI Access" />
              </ul>
            </div>

            <Link to="/register">
              <button className="w-full py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-black text-sm shadow-lg transition-all flex items-center justify-center gap-2 group">
                Upgrade Now <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </div>

        {/* --- CUSTOM / ENTERPRISE CTA --- */}
        <div className="mt-8 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-left">
            <div className="bg-white/5 p-3 rounded-xl">
              <Zap size={24} className="text-orange-500" />
            </div>
            <div>
              <h4 className="text-white font-bold text-md">Need more power?</h4>
              <p className="text-gray-400 text-xs mt-1">
                Require custom limits, advanced compliance, or a dedicated setup?
              </p>
            </div>
          </div>
          <a href="mailto:envdock@gmail.com" className="shrink-0 px-6 py-2 rounded-xl border border-white/10 hover:bg-white/5 text-white text-xs font-bold transition-all">
            Contact Sales
          </a>
        </div>

        {/* Compact Trust Footer */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 md:gap-12 opacity-60 scale-90 md:scale-100">
          <div className="flex items-center gap-2 text-gray-400">
            <ShieldCheck size={16} className="text-orange-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest">AES-256 Encrypted</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <RefreshCw size={16} className="text-blue-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Flexible Billing</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <Globe size={16} className="text-green-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest">99.9% Uptime</span>
          </div>
        </div>

      </div>
    </section>
  );
}